"use client";
import { IBlog, IBlogPost, ITheme, IUserData } from "@/types";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    IconAlignLeft,
    IconBrandFacebookFilled,
    IconBrandLinkedin,
    IconBrandReddit,
    IconBrandTelegram,
    IconBrandTumblr,
    IconBrandWhatsapp,
    IconBrandX,
    IconDotsVertical,
    IconLink,
    IconMail,
    IconShare3,
    IconUser,
} from "@tabler/icons-react";
import { UserURL } from "@/functions";
import { cn, getTextColorForBackground } from "@/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    EmailShareButton,
    FacebookShareButton,
    LinkedinShareButton,
    RedditShareButton,
    TelegramShareButton,
    TumblrShareButton,
    TwitterShareButton,
    WhatsappShareButton,
} from "react-share";
import { toast } from "sonner";
import BlogPostWrapper from "../PostWrapper";
import UserImage from "../../profile/heading/sections/UserImage";
import { Editor } from "../../BlockNote/DynamicEditor";
import { useBlogPostAnalytics } from "../useBlogPostAnalytics";
interface Props {
    post: IBlogPost;
    user: IUserData;
    impersonate?: boolean;
    theme: ITheme;
    blogTheme?: IBlog["theme"] | null;
    postPage?: boolean;
    blog?: IBlog;
    mainSite?: string;
    preview?: boolean;
    v2Theme?: IUserData["chosen_theme"]["blog"];
}
interface TOCItem {
    id: string;
    text: string;
    level: number;
}
export default function BlogPostV2({
    post,
    user,
    theme,
    blogTheme,
    postPage,
    blog,
    mainSite,
    preview,
    v2Theme,
}: Props) {
    const [tableOfContents, setTableOfContents] = useState<TOCItem[]>([]);
    const blogURL = "https://" + UserURL(user) + "/post/" + post.slug;
    const blogURLNoHttp = UserURL(user) + "/post/" + post.slug;
    const { trackShareClick } = useBlogPostAnalytics({
        post,
        blog,
        preview,
    });
    const color = v2Theme
        ? v2Theme.colors.primary_text
        : blogTheme?.button_text &&
            blogTheme?.button_text !== "rgba(0, 0, 0, 0)" &&
            blogTheme?.button_text !== ""
          ? blogTheme?.button_text
          : theme?.text_col_button || "white";
    const backgroundButton = v2Theme
        ? v2Theme.colors.primary
        : blogTheme?.button_bg &&
            blogTheme?.button_bg !== "rgba(0, 0, 0, 0)" &&
            blogTheme?.button_bg !== ""
          ? blogTheme?.button_bg
          : user.theme.button || "black";

    // Initial setup for scroll-padding-top (unrelated to analytics but important for UX)
    useEffect(() => {
        document.documentElement.style.scrollMarginTop = "180px";

        return () => {
            document.documentElement.style.scrollMarginTop = "";
        };
    }, []);

    // Heading extraction for Table of Contents (remains in BlogPost component)
    useEffect(() => {
        const extractHeadings = () => {
            const contentDiv = document.getElementById("Content");
            if (!contentDiv) return;

            const headings = contentDiv.querySelectorAll(
                "h1, h2, h3, h4, h5, h6"
            );
            const toc: TOCItem[] = [];
            headings.forEach((heading, index) => {
                const id = heading.id || `heading-${index}`;
                if (!heading.id) {
                    heading.id = id;
                }
                const level = parseInt(heading.tagName.substring(1));
                toc.push({
                    id,
                    text: heading.textContent || "",
                    level,
                });
            });
            setTableOfContents(toc);
        };

        const abortController = new AbortController();
        window.addEventListener(
            "BlockNote-Editor-Ready",
            {
                handleEvent: () => {
                    extractHeadings();
                },
            },
            { signal: abortController.signal }
        );
        return () => abortController.abort();
    }, []);

    const scrollToHeading = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <BlogPostWrapper
            post={post}
            user={user}
            theme={theme}
            blogTheme={blogTheme}
            blog={blog}
            mainSite={mainSite}
            v2Theme={v2Theme}
        >
            <div
                id="Content"
                className={cn(
                    "relative flex w-full max-w-full flex-col gap-2 p-2 md:flex-row",
                    "rounded-xl"
                )}
            >
                <div
                    className={cn(
                        "w-full overflow-y-auto p-2 md:w-9/12",
                        "rounded-xl"
                    )}
                    style={{
                        color: v2Theme
                            ? v2Theme.colors.primary_text
                            : getTextColorForBackground(
                                  blogTheme?.background || ""
                              ),
                        background: v2Theme
                            ? v2Theme.colors.primary
                            : theme.inset
                              ? "transparent"
                              : blogTheme?.background,
                        border: v2Theme
                            ? `2px solid ${v2Theme.colors.tertiary}`
                            : theme.inset
                              ? `2px solid ${blogTheme?.background}`
                              : "none",
                    }}
                >
                    <Editor
                        value={post.content}
                        id={"editor"}
                        key={"editor"}
                        readOnly
                    />
                </div>
                <div
                    className={cn(
                        "sticky top-4 ml-auto flex h-fit max-h-[calc(100vh-10rem)] w-full shrink-0 flex-col gap-6 overflow-y-auto p-2 md:w-3/12",
                        "rounded-xl",
                        postPage && "top-20"
                    )}
                    style={{
                        background: v2Theme
                            ? v2Theme.colors.primary
                            : user.theme.button,
                        color: v2Theme
                            ? v2Theme.colors.primary_text
                            : user.theme.text_col_button,
                        fontFamily: v2Theme?.design.font.body,
                    }}
                >
                    <a
                        href={"https://" + UserURL(post.author)}
                        className="space-y-2"
                        onClick={() => trackShareClick("author")}
                    >
                        <div className="flex items-center gap-2 font-bold">
                            <IconUser size={18} />
                            <p>Author</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <UserImage user={post.author} className="h-6 w-6" />
                            <div className="space-y-0">
                                <p className="text-sm font-bold">
                                    {post.author?.displayname}
                                </p>
                            </div>
                        </div>
                    </a>
                    {tableOfContents.length > 0 && (
                        <>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 font-bold">
                                    <IconAlignLeft size={18} />
                                    <p>On this page</p>
                                </div>
                                <ul className="list-inside list-disc space-y-1">
                                    {tableOfContents.map((item) => (
                                        <li
                                            key={item.id}
                                            className="list-item cursor-pointer select-none truncate hover:underline"
                                            style={{
                                                marginLeft: `${(item.level - 1) * 6}px`,
                                            }}
                                            onClick={() => {
                                                trackShareClick(
                                                    "TOC-" + item.text + "-TOC"
                                                );
                                                scrollToHeading(item.id);
                                            }}
                                        >
                                            {item.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 font-bold">
                            <IconShare3 size={18} />
                            <p>Share this post</p>
                        </div>
                        <div className="flex flex-wrap gap-1 md:grid md:grid-cols-5">
                            <div className="w-fit">
                                <Button
                                    id="Copy URL"
                                    className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl p-2 text-xl font-bold transition-all duration-150 hover:scale-100 `}
                                    size={"icon"}
                                    style={{
                                        background: "#a0a0a0",
                                        color: "white",
                                    }}
                                    onClick={async () => {
                                        try {
                                            toast.success(
                                                "Copied to clipboard!"
                                            );
                                            await navigator.clipboard.writeText(
                                                blogURL
                                            );
                                            trackShareClick("copy_url");
                                        } catch (err) {
                                            console.error(
                                                "Failed to copy text: ",
                                                err
                                            );
                                            toast.error(
                                                "Failed to copy to clipboard"
                                            );
                                        }
                                    }}
                                >
                                    <IconLink
                                        size={30}
                                        className="text-white"
                                    />
                                </Button>
                            </div>
                            <FacebookShareButton
                                url={blogURL}
                                className="w-fit"
                                onClick={() => trackShareClick("facebook")}
                            >
                                <div
                                    id="Facebook"
                                    className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#217cf4] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#217cf4]`}
                                >
                                    <IconBrandFacebookFilled
                                        size={30}
                                        fill="white"
                                    />
                                </div>
                            </FacebookShareButton>

                            <LinkedinShareButton
                                url={blogURL}
                                className="w-fit"
                                onClick={() => trackShareClick("linkedin")}
                            >
                                <div
                                    id="LinkedIn"
                                    className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#087fb7] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#087fb7]`}
                                >
                                    <IconBrandLinkedin
                                        size={30}
                                        className="text-white"
                                    />
                                </div>
                            </LinkedinShareButton>
                            <RedditShareButton
                                url={blogURL}
                                className="w-fit"
                                onClick={() => trackShareClick("reddit")}
                            >
                                <div
                                    id="Reddit"
                                    className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#ff4b08] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#ff4b08]`}
                                >
                                    <IconBrandReddit
                                        size={30}
                                        className="text-white"
                                    />
                                </div>
                            </RedditShareButton>
                            <div className="w-fit">
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button
                                            className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl p-2 text-xl font-bold transition-all duration-150 hover:scale-100 `}
                                            size={"icon"}
                                            style={{
                                                background: "#a0a0a0",
                                                color: "white",
                                            }}
                                        >
                                            <IconDotsVertical
                                                size={30}
                                                className="text-white"
                                            />
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent
                                        className="h-fit sm:h-fit"
                                        style={{
                                            borderColor: v2Theme
                                                ? v2Theme.colors.tertiary
                                                : user.theme.button,
                                            background: v2Theme
                                                ? v2Theme.colors.primary
                                                : user.theme.button,
                                        }}
                                    >
                                        <DialogHeader>
                                            <DialogTitle>Share</DialogTitle>
                                        </DialogHeader>
                                        <div className="h-full overflow-auto">
                                            <div
                                                className={cn(
                                                    "relative flex min-w-full items-center gap-2 bg-foreground/5 p-1"
                                                )}
                                                style={{
                                                    color: blogTheme?.button_text
                                                        ? blogTheme?.button_text
                                                        : color,
                                                }}
                                            >
                                                <div className="relative overflow-hidden">
                                                    <div className="noscroll overflow-x-auto whitespace-nowrap">
                                                        <p
                                                            className={cn(
                                                                "inline-block px-2 text-sm font-semibold"
                                                            )}
                                                        >
                                                            {blogURLNoHttp}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="ml-auto shrink-0">
                                                    <Button
                                                        id="Copy URL"
                                                        className={`justify-start gap-1 rounded-xl text-xl font-bold transition-all duration-150`}
                                                        size={"icon"}
                                                        style={{
                                                            background:
                                                                backgroundButton,
                                                            color: color,
                                                        }}
                                                        onClick={async () => {
                                                            try {
                                                                toast.success(
                                                                    "Copied to clipboard!"
                                                                );
                                                                await navigator.clipboard.writeText(
                                                                    blogURL
                                                                );
                                                                trackShareClick(
                                                                    "copy_url_dialog"
                                                                );
                                                            } catch (err) {
                                                                console.error(
                                                                    "Failed to copy text: ",
                                                                    err
                                                                );
                                                                toast.error(
                                                                    "Failed to copy to clipboard"
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <IconLink />
                                                    </Button>
                                                </div>
                                            </div>
                                            <Separator className="my-3" />
                                            <div className="flex flex-wrap gap-3">
                                                <FacebookShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "facebook_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Facebook"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#217cf4] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#217cf4]`}
                                                    >
                                                        <IconBrandFacebookFilled
                                                            size={30}
                                                        />
                                                    </div>
                                                </FacebookShareButton>
                                                <TwitterShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "x_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Twitter"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#000000] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#000000]`}
                                                    >
                                                        <IconBrandX size={30} />
                                                    </div>
                                                </TwitterShareButton>
                                                <LinkedinShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "linkedin_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="LinkedIn"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#087fb7] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#087fb7]`}
                                                    >
                                                        <IconBrandLinkedin
                                                            size={30}
                                                        />
                                                    </div>
                                                </LinkedinShareButton>
                                                <RedditShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "reddit_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Reddit"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#ff4b08] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#ff4b08]`}
                                                    >
                                                        <IconBrandReddit
                                                            size={30}
                                                        />
                                                    </div>
                                                </RedditShareButton>
                                                <TelegramShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "telegram_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Telegram"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#2fabea] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#2fabea]`}
                                                    >
                                                        <IconBrandTelegram
                                                            size={30}
                                                        />
                                                    </div>
                                                </TelegramShareButton>
                                                <TumblrShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "tumblr_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Tumblr"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#001935] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#001935]`}
                                                    >
                                                        <IconBrandTumblr
                                                            size={30}
                                                        />
                                                    </div>
                                                </TumblrShareButton>
                                                <WhatsappShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "whatsapp_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="Whatsapp"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl bg-[#31ab21] p-2 text-xl font-bold transition-all duration-150 hover:scale-100 hover:bg-[#31ab21]`}
                                                    >
                                                        <IconBrandWhatsapp
                                                            size={30}
                                                        />
                                                    </div>
                                                </WhatsappShareButton>
                                                <EmailShareButton
                                                    url={blogURL}
                                                    className="w-fit"
                                                    onClick={() =>
                                                        trackShareClick(
                                                            "email_dialog"
                                                        )
                                                    }
                                                >
                                                    <div
                                                        id="email"
                                                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-xl p-2 text-xl font-bold transition-all duration-150 hover:scale-100`}
                                                    >
                                                        <IconMail size={30} />
                                                    </div>
                                                </EmailShareButton>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BlogPostWrapper>
    );
}
